import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DRBFMSession,
  DRBFMWizardStep,
  DRBFMInputData,
  DRBFMAnalysisProgress,
  SessionResultsResponse,
  DRBFMWorkflowResponse,
  FlowProgress,
} from '../../types';
import { initialState, initialInputData, initialAnalysisProgress } from './drbfmSlice.state';

export type { DRBFMState } from './drbfmSlice.state';
export * from './drbfmSlice.selectors';

// ==============================================================================
// Slice
// ==============================================================================

const drbfmSlice = createSlice({
  name: 'drbfm',
  initialState,
  reducers: {
    // Session management
    setCurrentSession: (state, action: PayloadAction<DRBFMSession | null>) => {
      state.currentSession = action.payload;
    },

    clearSessionsError: (state) => {
      state.sessionsError = null;
    },

    // Wizard step management
    setWizardStep: (state, action: PayloadAction<DRBFMWizardStep>) => {
      state.wizardStep = action.payload;
    },

    nextWizardStep: (state) => {
      if (state.wizardStep === 'input') {
        state.wizardStep = 'processing';
      } else if (state.wizardStep === 'processing') {
        state.wizardStep = 'results';
      }
    },

    previousWizardStep: (state) => {
      if (state.wizardStep === 'results') {
        state.wizardStep = 'processing';
      } else if (state.wizardStep === 'processing') {
        state.wizardStep = 'input';
      }
    },

    resetWizard: (state) => {
      state.wizardStep = 'input';
      state.inputData = initialInputData;
      state.analysisProgress = initialAnalysisProgress;
      state.analysisResults = null;
      state.currentSession = null;
      state.workflow = null;
      state.workflowLoading = false;
      state.workflowError = null;
    },

    // Input data management
    setInputData: (state, action: PayloadAction<Partial<DRBFMInputData>>) => {
      state.inputData = { ...state.inputData, ...action.payload };
    },

    setItemName: (state, action: PayloadAction<string>) => {
      state.inputData.itemName = action.payload;
    },

    setTitle: (state, action: PayloadAction<string>) => {
      state.inputData.title = action.payload;
    },

    setOldSpecification: (state, action: PayloadAction<string>) => {
      state.inputData.oldSpecification = action.payload;
    },

    setNewSpecification: (state, action: PayloadAction<string>) => {
      state.inputData.newSpecification = action.payload;
    },

    setSpecOldDocumentId: (state, action: PayloadAction<string | undefined>) => {
      state.inputData.specOldDocumentId = action.payload;
    },

    setSpecNewDocumentId: (state, action: PayloadAction<string | undefined>) => {
      state.inputData.specNewDocumentId = action.payload;
    },

    clearInputData: (state) => {
      state.inputData = initialInputData;
    },

    // Analysis progress management
    startAnalysis: (state) => {
      state.analysisProgress = {
        isRunning: true,
        currentFlow: 0,
        overallProgress: 0,
        flowStatuses: {},
        messages: [],
        error: undefined,
      };
      state.wizardStep = 'processing';
    },

    updateAnalysisProgress: (state, action: PayloadAction<Partial<DRBFMAnalysisProgress>>) => {
      state.analysisProgress = { ...state.analysisProgress, ...action.payload };
    },

    setCurrentFlow: (state, action: PayloadAction<number>) => {
      state.analysisProgress.currentFlow = action.payload;
    },

    setOverallProgress: (state, action: PayloadAction<number>) => {
      state.analysisProgress.overallProgress = action.payload;
    },

    updateFlowStatus: (state, action: PayloadAction<{ flow: keyof FlowProgress; status: FlowProgress[keyof FlowProgress] }>) => {
      const { flow, status } = action.payload;
      state.analysisProgress.flowStatuses[flow] = status;
    },

    addAnalysisMessage: (state, action: PayloadAction<string>) => {
      state.analysisProgress.messages.push(action.payload);
    },

    setAnalysisError: (state, action: PayloadAction<string>) => {
      state.analysisProgress.error = action.payload;
      state.analysisProgress.isRunning = false;
    },

    completeAnalysis: (state) => {
      state.analysisProgress.isRunning = false;
      state.analysisProgress.overallProgress = 1;
      state.wizardStep = 'results';
    },

    cancelAnalysis: (state) => {
      state.analysisProgress.isRunning = false;
      state.wizardStep = 'input';
    },

    // Results management
    setAnalysisResults: (state, action: PayloadAction<SessionResultsResponse | null>) => {
      state.analysisResults = action.payload;
    },

    clearAnalysisResults: (state) => {
      state.analysisResults = null;
    },

    // Staged workflow management
    setWorkflowState: (state, action: PayloadAction<DRBFMWorkflowResponse | null>) => {
      state.workflow = action.payload;
      state.workflowLoading = false;
      state.workflowError = null;
    },

    setWorkflowLoading: (state, action: PayloadAction<boolean>) => {
      state.workflowLoading = action.payload;
      if (action.payload) {
        state.workflowError = null;
      }
    },

    setWorkflowError: (state, action: PayloadAction<string | null>) => {
      state.workflowError = action.payload;
      state.workflowLoading = false;
    },

    clearWorkflowState: (state) => {
      state.workflow = null;
      state.workflowLoading = false;
      state.workflowError = null;
    },

    // Export state management
    startExport: (state) => {
      state.isExporting = true;
      state.exportError = null;
    },

    completeExport: (state) => {
      state.isExporting = false;
    },

    setExportError: (state, action: PayloadAction<string>) => {
      state.isExporting = false;
      state.exportError = action.payload;
    },

    clearExportError: (state) => {
      state.exportError = null;
    },
  },
});

// ==============================================================================
// Actions
// ==============================================================================

export const {
  // Session management
  setCurrentSession,
  clearSessionsError,
  // Wizard step management
  setWizardStep,
  nextWizardStep,
  previousWizardStep,
  resetWizard,
  // Input data management
  setInputData,
  setItemName,
  setTitle,
  setOldSpecification,
  setNewSpecification,
  setSpecOldDocumentId,
  setSpecNewDocumentId,
  clearInputData,
  // Analysis progress management
  startAnalysis,
  updateAnalysisProgress,
  setCurrentFlow,
  setOverallProgress,
  updateFlowStatus,
  addAnalysisMessage,
  setAnalysisError,
  completeAnalysis,
  cancelAnalysis,
  // Results management
  setAnalysisResults,
  clearAnalysisResults,
  // Staged workflow management
  setWorkflowState,
  setWorkflowLoading,
  setWorkflowError,
  clearWorkflowState,
  // Export state management
  startExport,
  completeExport,
  setExportError,
  clearExportError,
} = drbfmSlice.actions;

export default drbfmSlice.reducer;
